/** `[lng, lat]` — the order TomTom / GeoJSON use. */
export type LngLat = [number, number];

/**
 * A coordinate worth drawing: finite, in range, and not (0, 0) — new trucks
 * are created at 0/0 before their first position report, and a marker in the
 * Gulf of Guinea is worse than no marker.
 */
export function isValidCoordinate(lat: unknown, lng: unknown): boolean {
  if (typeof lat !== 'number' || typeof lng !== 'number') return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  return !(lat === 0 && lng === 0);
}

export function toLngLat(lat: unknown, lng: unknown): LngLat | null {
  return isValidCoordinate(lat, lng) ? [lng as number, lat as number] : null;
}

/** South-west / north-east corners of a set of points, or null when empty. */
export function boundsOf(points: readonly LngLat[]): [LngLat, LngLat] | null {
  if (points.length === 0) return null;
  let minLng = points[0][0];
  let maxLng = points[0][0];
  let minLat = points[0][1];
  let maxLat = points[0][1];
  for (const [lng, lat] of points) {
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

/** Great-circle distance in km. */
export function haversineKm(a: LngLat, b: LngLat): number {
  const rad = Math.PI / 180;
  const dLat = (b[1] - a[1]) * rad;
  const dLng = (b[0] - a[0]) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)));
}

function median(values: number[]): number {
  const sorted = [...values].sort((x, y) => x - y);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Splits points into the main cluster (within `radiusKm` of the median point)
 * and far-away outliers, so one mistyped location doesn't zoom the map out to
 * a continent. Nothing is hidden: the map offers "show all" for outliers.
 */
export function clusterPoints(points: readonly LngLat[], radiusKm = 250): { inliers: LngLat[]; outliers: LngLat[] } {
  if (points.length <= 2) return { inliers: [...points], outliers: [] };
  const center: LngLat = [median(points.map((p) => p[0])), median(points.map((p) => p[1]))];
  const inliers: LngLat[] = [];
  const outliers: LngLat[] = [];
  for (const p of points) (haversineKm(center, p) <= radiusKm ? inliers : outliers).push(p);
  return inliers.length > 0 ? { inliers, outliers } : { inliers: [...points], outliers: [] };
}

/** Coordinates of a GeoJSON LineString (`[lng, lat]`), or null when it isn't one. */
export function lineCoordinates(geometry: unknown): LngLat[] | null {
  if (!geometry || typeof geometry !== 'object') return null;
  const g = geometry as { type?: unknown; coordinates?: unknown };
  if (g.type !== 'LineString' || !Array.isArray(g.coordinates)) return null;
  const coords = g.coordinates.filter(
    (c): c is LngLat => Array.isArray(c) && c.length >= 2 && isValidCoordinate(c[1], c[0]),
  );
  return coords.length >= 2 ? coords.map(([lng, lat]) => [lng, lat] as LngLat) : null;
}
